import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Phone, Copy, CheckCircle2, AlertTriangle, AlertOctagon, ExternalLink } from "lucide-react";

const A2P_ERROR_CODES = new Set(["30032", "30034", "30035", "30036", "30044", "30103"]);

export default function TwilioStatusCard() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/twilio/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  if (!status) return null;

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.info("Copied"); } catch {}
  };

  const issue = status.delivery_issue;
  const a2pFromRecent = (status.recent_messages || []).find((m) => m.error_code && A2P_ERROR_CODES.has(m.error_code));
  const a2pIssue = (issue && A2P_ERROR_CODES.has(issue.error_code)) ? issue : a2pFromRecent && { error_code: a2pFromRecent.error_code, error_message: a2pFromRecent.error_message, sample_sid: a2pFromRecent.sid };
  const isA2P = Boolean(a2pIssue);

  return (
    <div className="space-y-3">
      {isA2P && (
        <Card className="bg-rose-500/10 border-rose-500/40 p-5" data-testid="twilio-a2p-warning">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-400 flex items-center justify-center shrink-0">
              <AlertOctagon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-display text-lg font-semibold text-rose-100">SMS delivery blocked — A2P 10DLC required</h3>
                <Badge className="bg-rose-500/20 text-rose-200 border border-rose-500/40 font-mono">Error {a2pIssue.error_code}</Badge>
              </div>
              <p className="text-sm text-rose-100/90">
                Twilio accepted our messages but delivery to US phones is being blocked because your long-code number
                (<span className="font-mono">{status.phone_number}</span>) isn't registered for
                A2P&nbsp;10DLC. Voice calls still work — only outbound SMS is affected.
              </p>
              <p className="text-sm text-rose-100/90 mt-2">
                Fix it in Twilio Console → Messaging → Regulatory Compliance → Register a Brand + Campaign
                (usually approved in 1–3 business days).
              </p>
              <a
                href="https://console.twilio.com/us1/develop/sms/regulatory-compliance/a2p-10dlc-onboarding"
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300 mt-2 font-medium"
                data-testid="a2p-console-link">
                Open A2P registration <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </Card>
      )}

      <Card
        className={`p-5 border ${
          status.configured && status.company_number_assigned
            ? "bg-emerald-500/5 border-emerald-500/30"
            : "bg-amber-500/5 border-amber-500/30"
        }`}
        data-testid="twilio-status-card"
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
              status.configured && status.company_number_assigned ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"
            }`}
          >
            {status.configured && status.company_number_assigned ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display text-lg font-semibold">Twilio SMS + Voice</h3>
              {status.configured && status.company_number_assigned ? (
                <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Live</Badge>
              ) : (
                <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30">{status.configured ? "No number" : "Not configured"}</Badge>
              )}
            </div>
            {status.configured && status.company_number_assigned ? (
              <>
                <p className="text-sm text-slate-300">
                  Every missed call to <span className="font-mono text-emerald-400">{status.phone_number}</span> auto-creates a lead for{" "}
                  <span className="text-white font-semibold">{status.company_name}</span>{String(status.company_name || "").endsWith(".") ? "" : "."}
                </p>
                <div className="mt-3 grid gap-2 text-xs">
                  <button onClick={() => copy(status.webhooks.sms)} className="flex items-center gap-2 text-left p-2 rounded bg-slate-900/60 border border-slate-800 hover:border-emerald-500/40 transition-colors" data-testid="copy-sms-webhook">
                    <Phone className="w-3 h-3 text-emerald-400 shrink-0" />
                    <span className="font-mono text-slate-300 flex-1 truncate">{status.webhooks.sms}</span>
                    <Copy className="w-3 h-3 text-slate-500" />
                  </button>
                  <button onClick={() => copy(status.webhooks.voice)} className="flex items-center gap-2 text-left p-2 rounded bg-slate-900/60 border border-slate-800 hover:border-emerald-500/40 transition-colors" data-testid="copy-voice-webhook">
                    <Phone className="w-3 h-3 text-emerald-400 shrink-0" />
                    <span className="font-mono text-slate-300 flex-1 truncate">{status.webhooks.voice}</span>
                    <Copy className="w-3 h-3 text-slate-500" />
                  </button>
                </div>
                {status.recent_messages && status.recent_messages.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-800">
                    <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Last 5 outbound SMS</p>
                    <div className="space-y-1">
                      {status.recent_messages.map((m) => (
                        <div key={m.sid} className="flex items-center gap-2 text-xs" data-testid={`recent-sms-${m.sid}`}>
                          <StatusPill status={m.status} errorCode={m.error_code} />
                          <span className="font-mono text-slate-400">{m.to}</span>
                          {m.error_code && <span className="text-rose-400 font-mono">err {m.error_code}</span>}
                          <span className="text-slate-600 font-mono ml-auto">{m.sent_at ? new Date(m.sent_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-sm text-slate-300" data-testid="twilio-not-ready-text">
                  {status.configured
                    ? "Your company doesn't have a RoofLead number yet, so calls and texts aren't active. Add your number in Settings."
                    : "Twilio credentials aren't set on the server yet — SMS is running in simulation."}
                </p>
                <div className="mt-3 grid gap-2 text-xs">
                  <p className="text-slate-400 font-mono">Webhook URLs (paste into Twilio Console when ready):</p>
                  <button onClick={() => copy(status.webhooks.sms)} className="flex items-center gap-2 text-left p-2 rounded bg-slate-900/60 border border-slate-800" data-testid="copy-sms-webhook">
                    <span className="text-slate-500 shrink-0">SMS →</span>
                    <span className="font-mono text-slate-300 flex-1 truncate">{status.webhooks.sms}</span>
                    <Copy className="w-3 h-3 text-slate-500" />
                  </button>
                  <button onClick={() => copy(status.webhooks.voice)} className="flex items-center gap-2 text-left p-2 rounded bg-slate-900/60 border border-slate-800" data-testid="copy-voice-webhook">
                    <span className="text-slate-500 shrink-0">Voice →</span>
                    <span className="font-mono text-slate-300 flex-1 truncate">{status.webhooks.voice}</span>
                    <Copy className="w-3 h-3 text-slate-500" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function StatusPill({ status, errorCode }) {
  if (errorCode) return <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 text-[10px] uppercase tracking-wider">Failed</Badge>;
  if (status === "delivered") return <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px] uppercase tracking-wider">Delivered</Badge>;
  if (status === "sent") return <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/40 text-[10px] uppercase tracking-wider">Sent</Badge>;
  if (status === "queued" || status === "accepted" || status === "sending") return <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px] uppercase tracking-wider">Pending</Badge>;
  if (status === "undelivered" || status === "failed") return <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 text-[10px] uppercase tracking-wider">{status}</Badge>;
  return <Badge className="bg-slate-700 text-slate-300 border-slate-600 text-[10px] uppercase tracking-wider">{status || "—"}</Badge>;
}
